import Vue from 'vue';
import { Module } from 'vuex';

import {
  CategorizedCoursesBySubcategory,
  CategorizedCoursesListState,
  CategorizedCoursesState,
  CategoryCoursesMap,
  Course,
  CourseCategory,
  CourseSubcategory,
  CoursesBySubcategory,
  RootState,
} from 'frontend/types';

import { roughNumberFormat } from 'frontend/utils/formatting';
import { postData } from 'frontend/utils/ajax';

interface CourseCategoryMap {
  [courseId: number]: CourseCategory;
}

interface CourseSubcategoryMap {
  [courseId: number]: CourseSubcategory;
}

interface CourseBooleanMap {
  [courseId: number]: boolean;
}

type CategorizedCoursesBySubcategoryFunc = () => CategorizedCoursesBySubcategory[];

const categorizedCourses: Module<CategorizedCoursesListState, RootState> = {
  namespaced: true,
  state: () => ({
    items: [] as CategorizedCoursesState[],
  }),
  getters: {
    courseCategories(state): CourseCategoryMap {
      const courseCategoryMap: CourseCategoryMap = {};
      state.items.forEach(({ category, courses_by_sub_category }) => {
        courses_by_sub_category.forEach(({ sub_category, courses }) => {
          courses.forEach((course: Course) => {
            courseCategoryMap[course.course_id] = category;
          });
        });
      });
      return courseCategoryMap;
    },
    courseSubcategories(state): CourseSubcategoryMap {
      const courseSubcategoryMap: CourseSubcategoryMap = {};
      state.items.forEach(({ courses_by_sub_category }) => {
        courses_by_sub_category.forEach(({ sub_category, courses }) => {
          courses.forEach((course: Course) => {
            courseSubcategoryMap[course.course_id] = sub_category;
          });
        });
      });
      return courseSubcategoryMap;
    },
    courseIsPreviewable(state): CourseBooleanMap {
      const previewableCourseMap: CourseBooleanMap = {};
      state.items.forEach(({ courses_by_sub_category }) => {
        courses_by_sub_category.forEach(({ courses }) => {
          courses.forEach((course: Course) => {
            previewableCourseMap[course.course_id] = course.user_can_preview;
          });
        });
      });
      return previewableCourseMap;
    },
    courseIsStarted(state): CourseBooleanMap {
      const startedCourseMap: CourseBooleanMap = {};
      state.items.forEach(({ courses_by_sub_category }) => {
        courses_by_sub_category.forEach(({ courses }) => {
          courses.forEach((course: Course) => {
            startedCourseMap[course.course_id] = course.is_started === true;
          });
        });
      });
      return startedCourseMap;
    },
    courseIsComplete(state): CourseBooleanMap {
      const completedCourseMap: CourseBooleanMap = {};
      state.items.forEach(({ courses_by_sub_category }) => {
        courses_by_sub_category.forEach(({ courses }: { courses: Course[] }) => {
          courses.forEach((course: Course) => {
            completedCourseMap[course.course_id] = course.is_complete === true;
          });
        });
      });
      return completedCourseMap;
    },
    getCategoryForCourse(state, getters): (course: Course) => CourseCategory | undefined {
      return course => getters.courseCategories[course.course_id];
    },
    getSubcategoryForCourse(state, getters): (course: Course) => CourseSubcategory | undefined {
      return course => getters.courseSubcategories[course.course_id];
    },
    getUserCanPreviewForCourse(state, getters): (course: Course) => boolean {
      return course => getters.courseIsPreviewable[course.course_id] === true;
    },
    getUserStartedCourse(state, getters): (course: Course) => boolean {
      return course => getters.courseIsStarted[course.course_id] === true;
    },
    getUserCompletedCourse(state, getters): (course: Course) => boolean {
      return course => getters.courseIsComplete[course.course_id] === true;
    },
    getCategoryShortName(): (category: CourseCategory | undefined) => string {
      return category => {
        if (category) {
          return category.name.replace('Computer Science', 'CS');
        }
        return '';
      };
    },
    groupedCourses(state): CategorizedCoursesBySubcategoryFunc {
      return (): CategorizedCoursesBySubcategory[] => {
        if (state.items.length) {
          let counter = 0;
          return state.items.map(
            (categorizedCoursesState: CategorizedCoursesState, index: number) => {
              const coursesBySubcategory: CoursesBySubcategory = {};

              categorizedCoursesState.courses_by_sub_category.forEach(
                ({ sub_category, courses }) => {
                  coursesBySubcategory[sub_category.name] = {
                    courses,
                    groupIndex: counter,
                  };
                  counter += 1;
                },
              );

              return {
                coursesBySubcategory,
                category: categorizedCoursesState.category,
              } as CategorizedCoursesBySubcategory;
            },
          );
        }
        return [];
      };
    },
    flattenedCourses(state): Course[] {
      if (state.items.length) {
        const result: Course[] = [];
        const includedCourseIds: number[] = [];
        state.items.forEach((categorizedCoursesState: CategorizedCoursesState) => {
          categorizedCoursesState.courses_by_sub_category.forEach(({ courses }) => {
            courses.forEach(course => {
              if (includedCourseIds.indexOf(course.course_id) < 0) {
                includedCourseIds.push(course.course_id);
                result.push(course);
              }
            });
          });
        });

        return result;
      }
      return [];
    },
    coursesByCategory(state, getters): CategoryCoursesMap {
      const results: CategoryCoursesMap = {};
      getters.flattenedCourses.forEach((c: Course) => {
        const category: CourseCategory = getters.getCategoryForCourse(c);
        if (!results[category.name]) {
          results[category.name] = [];
        }
        results[category.name].push(c);
      });
      return results;
    },
    roughNumCourses(state, getters): string {
      const numCourses = Object.keys(getters.courseCategories).length || 70;
      return roughNumberFormat(numCourses);
    },
  },
  mutations: {
    setValue(state: CategorizedCoursesListState, value: CategorizedCoursesState[]): void {
      Vue.set(state, 'items', value);
    },
    setNotifyComingSoon(state, { courseId, notifyWhenPublished }) {
      state.items.forEach(({ courses_by_sub_category }) => {
        courses_by_sub_category.forEach(({ courses }) => {
          courses.forEach(c => {
            if (c.course_id === courseId) {
              c.notify_when_published = notifyWhenPublished;
            }
          });
        });
      });
    },
  },
  actions: {
    notifyComingSoon({ commit }, course): Promise<any> {
      const url = `/api/v1/quizzes/coming_soon_notification/exploration/${course.slug}/`;
      const data: { unset_notification?: boolean } = {};
      if (course.notify_when_published) {
        data.unset_notification = true;
      }
      return postData(url, data).then(response => {
        if (response.success) {
          const params = {
            courseId: course.course_id,
            notifyWhenPublished: response.data.notify_when_published,
          };
          commit('setNotifyComingSoon', params);
          commit('recentCourses/setNotifyComingSoon', params, { root: true });
          commit('secondaryFeaturedCourses/setNotifyComingSoon', params, { root: true });
        }
      });
    },
  },
};

export default categorizedCourses;
